import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { IMAGE_URL } from "../../../../utils/api-config";
import { GetOpdEntries } from "../../../../api/opd/opdEntryAPI";
import { GetBillingCharges } from "../../../../api/masters/billingChargesAPI";
import { GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { GetOpdChargesEntryById, UpdateOpdChargesEntry } from "../../../../api/opd/opdChargesEntryAPI";
import { GetConsultants } from "../../../../api/masters/consultantAPI";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  opdEntryId: yup.string().required("OPD is required"),
  // ipdEntryId: yup.string(),
  date: yup.date().required("Date is required"),
  rateMode: yup.string().required("Rate Mode is required"),
  billingCharges: yup.array().of(
    yup.object().shape({
      billingChargesId: yup.string().required("Billing Charge is required"),
      doctorId: yup.string().required("Doctor is required"),
      unit: yup.number().required("Unit is required"),
      rate: yup.number().required("Rate is required"),
      amount: yup.number().required("Amount is required"),
    })
  ),
  grossAmount: yup
    .number()
    .typeError("Gross Amount must be a number")
    .required("Gross Amount is required"),
  totalAmount: yup
    .number()
    .typeError("Total Amount must be a number")
    .required("Total Amount is required")
});

const EditOpdChargesEntry = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	const [consultants, setConsultants] = useState([]);

	const [opdEntries, setOpdEntries] = useState([]);
	const [ipdEntries, setIpdEntries] = useState([]);
	const [billingCharges, setBillingCharges] = useState([]);
	const [patientDetails, setPatientDetails] = useState({});

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		control,
		watch,
		formState: { errors } } = useForm({
			resolver: yupResolver(schema),
			defaultValues: {
				billingCharges: [
					{
						billingChargesId: "",
						doctorName: "",
						unit: 1,
						rate: 0,
						amount: 0,
						refundAmount: 0,
					}
				]
			}
		});

	const {
		fields: billingChargesFields,
		append: appendBillingCharges,
		remove: removeBillingCharges
	} = useFieldArray({
		control,
		name: "billingCharges"
	});

	const fetchOpdEntries = async () => {
		try {
			const response = await GetOpdEntries();
			if (response.success) {
				setOpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchIpdEntries = async () => {
		try {
			const response = await GetIpdEntries();
			if (response.success) {
				setIpdEntries(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchConsultants = async () => {
		try {
			const response = await GetConsultants();
			if (response.success) {
				setConsultants(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const fetchBillingCharges = async () => {
		try {
			const response = await GetBillingCharges();
			if (response.success) {
				setBillingCharges(response.data);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error.message);
		}
	};

	const calculateAll = () => {
		const rows = getValues("billingCharges");

		// 1. Row amounts
		rows.forEach((row, index) => {
			const unit = parseFloat(row?.unit) || 0;
			const rate = parseFloat(row?.rate) || 0;
			const amount = unit * rate;

			if (row?.amount !== amount) {
				setValue(`billingCharges.${index}.amount`, amount, { shouldDirty: false });
			}
		});

		// 2. Gross Amount
		const gross = rows.reduce(
			(sum, row) =>
				sum + (parseFloat(row?.amount) || 0) - (parseFloat(row?.refundAmount) || 0),
			0
		);

		if (getValues("grossAmount") !== gross) {
			setValue("grossAmount", gross, { shouldDirty: false });
		}

		// 3. Discount
		const discountPercent = parseFloat(getValues("discountPercentage")) || 0;

		if (discountPercent > 0) {
			const discountAmount = (gross * discountPercent) / 100;

			if (getValues("discountAmount") !== discountAmount) {
				setValue("discountAmount", discountAmount, { shouldDirty: false });
			}
		}

		const finalDiscount = parseFloat(getValues("discountAmount")) || 0;

		const totalAfterDiscount = gross - finalDiscount;

		if (getValues("totalAmount") !== totalAfterDiscount) {
			setValue("totalAmount", totalAfterDiscount, { shouldDirty: false });
		}
	};

	const billingRows = useWatch({ control, name: "billingCharges" });
	const discountPercentage = watch("discountPercentage");
	const discountAmount = watch("discountAmount"); // only if user edits manually
	const grossAmount = watch("grossAmount");

	useEffect(() => {
		calculateAll();
	}, [billingRows, discountPercentage, discountAmount]);

	const fetchData = async () => {
		try {
			const response = await GetOpdChargesEntryById(id); // Make sure login function returns token
			if (response.success) {
				Object.entries(response.data).forEach(([key, value]) => {
					if (["date"].includes(key) && value) {
						setValue(key, value.split("T")[0]); // keeps YYYY-MM-DD
					} else {
						setValue(key, value);
					}
				});

				setPatientDetails(response.data?.opdEntryId || response.data?.ipdEntryId || {});
				setValue('opdEntryId', response.data?.opdEntryId?._id || '');
				setValue('ipdEntryId', response.data?.ipdEntryId?._id || '');

			} else {
				// setError(response.message);
				showToast("error", response.message);
			}
		} catch (error) {
			// setError(error); // Handle login errors
			showToast("error", error);
		}
	};

	// Fetch existing data
	useEffect(
		() => {
			if (id) {
				fetchIpdEntries().then(() => {
					fetchOpdEntries().then(() => {
						fetchConsultants().then(() => {
							fetchBillingCharges().then(() => {
								fetchData();
							});
						});
					});
				});
			}
		},
		[id, setValue]
	);

	const onSubmit = async data => {
		try {
			dispatch({ type: "LOADER_START" });

			const response = await UpdateOpdChargesEntry(id, data);
			if (response.success) {
				showToast("success", response.message);
			} else {
				showToast("error", response.message);
			}
		} catch (error) {
			showToast("error", error);
		} finally {
			dispatch({ type: "LOADER_STOP" });
		}
	};

  const formRef = useRef(null);
        useKeyboardSubmit(handleSubmit, onSubmit);
        useEnterNavigation(formRef);

	return (
		<Fragment>
			<Breadcrumb parent="OPD Charges" title="Add OPD Charges Entry" />
			<div className="container-fluid">
				<div className="row">
					<div className="col-sm-12">
						<div className="card">
							<div className="card-header d-flex justify-content-between">
								<h5>Add OPD Charges Entry</h5>
								<Link
									to={`/opd/transaction/opd-charges-entry/list`}
								>
									<button className="btn btn-primary" id="list">
										List OPD Charges Entry
									</button>
									<UncontrolledTooltip placement="top" target="list">
										List OPD Charges Entry
									</UncontrolledTooltip>
								</Link>
							</div>

							<form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* Name */}
                    <div className="col-6">
                      <div className="form-group">
                        <label>Doc No *</label>
                        <input
                          className="form-control"
                          {...register("docNo")}
                          placeholder="Doc No"
                          readOnly
                        />
                        {errors.docNo &&
                          <p className="text-danger">
                            {errors.docNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          defaultValue={new Date().toISOString().split("T")[0]}
                          // min={new Date().toISOString().split("T")[0]}
                          {...register("date")}
                          placeholder="Date"
                        />
                        {errors.date &&
                          <p className="text-danger">
                            {errors.date.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Opd No *</label>
                        <select
                          className="form-control"
                          {...register("opdEntryId", {
                            onChange: (e) => {
                              setPatientDetails(opdEntries.find(opd => opd._id === e.target.value));
                              setValue('ipdEntryId', '');
                            }
                          })}
                        >
                          <option value="">Select Opd No</option>
                          {opdEntries.map(opd =>
                            <option key={opd._id} value={opd._id}>
                              {opd.opdNo}
                            </option>
                          )}
                        </select>
                        {errors.opdEntryId &&
                          <p className="text-danger">
                            {errors.opdEntryId.message}
                          </p>}
                      </div>
                    </div>

                    {/* <div className="col-6">
                      <div className="form-group">
                        <label>Ipd No</label>
                        <select
                          className="form-control"
                          {...register("ipdEntryId", {
                            onChange: (e) => {
                              setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                              setValue('opdEntryId', '');
                            }
                          })}
                        >
                          <option value="">Select Ipd No</option>
                          {ipdEntries.map(ipd =>
                            <option key={ipd._id} value={ipd._id}>
                              {ipd.ipdNo}
                            </option>
                          )}
                        </select>
                      </div>
                    </div> */}

                    <div className="col-6">
                      <div className="form-group">
                        <label>Rate Mode</label>
                        <select
                          className="form-control"
                          {...register("rateMode")}
                          placeholder="Rate Mode"
                        >
                          <option value="Normal">Normal</option>
                          <option value="International">International</option>
                        </select>
                        {errors.rateMode &&
                          <p className="text-danger">
                            {errors.rateMode.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Patient Details
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>UHID No</label>
                        <input
                          className="form-control"
                          placeholder="UHID No"
                          value={patientDetails?.patientId?.uhidNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Opd No</label>
                        <input
                          className="form-control"
                          placeholder="Opd No"
                          value={patientDetails?.opdNo}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Patient</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.patientName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>guardian Name</label>
                        <input
                          className="form-control"
                          placeholder="guardian Name"
                          value={patientDetails?.patientId?.guardianName}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Age</label>
                        <input
                          className="form-control"
                          placeholder="Patient Name"
                          value={patientDetails?.patientId?.age}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Case</label>
                        <input
                          className="form-control"
                          placeholder="Case"
                          value={patientDetails?.caseId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Consultant Doctor</label>
                        <input
                          className="form-control"
                          placeholder="Consultant Doctor"
                          value={patientDetails?.consultantDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>ReferenceDoctor</label>
                        <input
                          className="form-control"
                          placeholder="ReferenceDoctor"
                          value={patientDetails?.referenceDoctorId?.name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          className="form-control"
                          placeholder="Address"
                          value={patientDetails?.patientId?.address}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <h5 className="mt-3 mb-3 text-primary">
                      Billing Charges
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-12 my-3">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Billing Charges</th>
                            <th>Doctor Name</th>
                            <th>Unit</th>
                            <th>Rate</th>
                            <th>Amount</th>
                            <th>Refund</th>
                            <th>Refund Amount</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {billingChargesFields.map((field, index) => (
                            <tr key={field.id}>
                              <td>
                                <select
                                  className="form-control"
                                  {...register(`billingCharges.${index}.billingChargesId`, {
                                    onChange: (e) => {
                                      const selectedCharge = billingCharges.find(
                                        (c) => c._id === e.target.value
                                      );

                                      if (selectedCharge) {
                                        // Set rate from master
                                        setValue(
                                          `billingCharges.${index}.rate`,
                                          selectedCharge.rate || 0
                                        );

                                        // Store rateEditable flag in form state
                                        setValue(
                                          `billingCharges.${index}.rateEditable`,
                                          selectedCharge.rateEditable
                                        );
                                      }
                                    }
                                  })}
                                >

                                  <option value="">Select Charge Name</option>
                                  {billingCharges.map((charge) => (
                                    <option key={charge._id} value={charge._id}>
                                      {charge.name}
                                    </option>
                                  ))}
                                </select>
                                {errors.billingCharges?.[index]?.billingChargesId && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].billingChargesId.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <select
                                  className="form-control"
                                  type="text"
                                  {...register(`billingCharges.${index}.doctorId`)}
                                  placeholder="Doctor Name"
                                >
                                  <option value="">Select Doctor Name</option>
                                  {consultants.map((consultant) => (
                                    <option key={consultant._id} value={consultant._id}>
                                      {consultant.name}
                                    </option>
                                  ))}
                                </select>
                                {errors.billingCharges?.[index]?.doctorId && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].doctorId.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="number"
                                  {...register(`billingCharges.${index}.unit`)}
                                  placeholder="Unit"
                                />
                                {errors.billingCharges?.[index]?.unit && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].unit.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="number"
                                  {...register(`billingCharges.${index}.rate`)}
                                  placeholder="Rate"
                                  readOnly={!billingRows?.[index]?.rateEditable}
                                />

                                {errors.billingCharges?.[index]?.rate && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].rate.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="number"
                                  {...register(`billingCharges.${index}.amount`)}
                                  readOnly
                                  placeholder="Amount"
                                />
                                {errors.billingCharges?.[index]?.amount && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].amount.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <select
                                  className="form-control"
                                  {...register(`billingCharges.${index}.refund`, {
                                    onChange: (e) => {
                                      if (e.target.value === "No") {
                                        setValue(`billingCharges.${index}.refundAmount`, 0);
                                      }
                                    }
                                  })}
                                >
                                  <option value="No">No</option>
                                  <option value="Yes">Yes</option>
                                </select>
                                {errors.billingCharges?.[index]?.refund && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].refund.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <input
                                  className="form-control"
                                  type="number"
                                  {...register(`billingCharges.${index}.refundAmount`)}
                                  placeholder="Refund Amount"
                                  disabled={billingRows?.[index]?.refund !== "Yes"}
                                />
                                {errors.billingCharges?.[index]?.refundAmount && (
                                  <p className="text-danger">
                                    {errors.billingCharges[index].refundAmount.message}
                                  </p>
                                )}
                              </td>

                              <td>
                                <div className="d-flex">

                                  <button
                                    type="button"
                                    className="btn btn-danger me-2"
                                    onClick={() => billingChargesFields.length > 1 && removeBillingCharges(index)}
                                  >
                                    -
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() =>
                                      appendBillingCharges({
                                        billingChargesId: "",
                                        doctorName: "",
                                        unit: "",
                                        rate: "",
                                        amount: "",
                                        refund: "No",
                                        refundAmount: "",
                                      })
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>


                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label>Gross Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register("grossAmount")}
                          placeholder="Gross Amount"
                          readOnly
                        />
                        {errors.grossAmount &&
                          <p className="text-danger">
                            {errors.grossAmount.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Discount Percentage</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          {...register("discountPercentage", {
                            onChange: (e) => {
                              if (e.target.value > 100) {
                                e.target.value = 100;
                              }
                              const discountAmount = (e.target.value * grossAmount / 100).toFixed(2);
                              setValue("discountAmount", discountAmount);
                            }
                          })}
                          placeholder="Discount Percentage"
                        />
                        {errors.discountPercentage &&
                          <p className="text-danger">
                            {errors.discountPercentage.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Discount Amount</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          {...register("discountAmount", {
                            onChange: (e) => {
                              const discountPercentage = (e.target.value * 100 / grossAmount).toFixed(2);
                              setValue("discountPercentage", discountPercentage);
                            }
                          })}
                          placeholder="Discount Amount"
                        />
                        {errors.discountAmount &&
                          <p className="text-danger">
                            {errors.discountAmount.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Discount By</label>
                        <input
                          className="form-control"
                          {...register("discountBy")}
                          placeholder="Discount By"
                        />
                        {errors.discountBy &&
                          <p className="text-danger">
                            {errors.discountBy.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Total Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register("totalAmount")}
                          placeholder="Total Amount"
                          readOnly
                        />
                        {errors.totalAmount &&
                          <p className="text-danger">
                            {errors.totalAmount.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Payment Mode *</label>
                        <select
                          className="form-control"
                          {...register("paymentMode")}
                          placeholder="Payment Mode"
                        >
                          <option value="">Select Payment Mode</option>
                          <option value="Cash">Cash</option>
                          <option value="Online">Online</option>
                        </select>
                        {errors.paymentMode &&
                          <p className="text-danger">
                            {errors.paymentMode.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="form-group">
                        <label>Debit Account</label>
                        <input
                          className="form-control"
                          {...register("debitAccount")}
                          placeholder="Debit Account"
                        />
                        {errors.debitAccount &&
                          <p className="text-danger">
                            {errors.debitAccount.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default EditOpdChargesEntry;
